//* 판매자정보(admin 전용) - shop에 연결된 유저 계정 목록
//* admin은 계정별 전화번호 수정, shop과의 연결해제가 가능하다.

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { updateShop } from '@shared/api/shop/shop.controller';
import { sellerShopHookUrl } from '@shared/api/shop/shop.hook';
import { ShopData, ShopUserData } from '@shared/api/shop/shop.interface';
import { adminSellerUserSearch, adminUpdateUser } from '@shared/api/user/user.controller';
import { User } from '@shared/api/user/user.interface';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import Modal from '@shared/components/Modal';
import TelField from '@shared/components/TelField';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { UserRole } from '@shared/types';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useAsyncFn } from 'react-use';

interface IProps {
  shop: ShopData;
}

const roleLabelMap: Partial<Record<UserRole, string>> = {
  [UserRole.Admin]: '시스템 관리자',
  [UserRole.StoreSellerAdmin]: '판매자 관리자',
  [UserRole.StoreSeller]: '판매자',
  [UserRole.StoreSellerWorker]: '판매자 직원',
};

const roleBadges = (roles: UserRole[] | undefined) =>
  (roles ?? [])
    .filter((role) => roleLabelMap[role])
    .map((role) => (
      <span
        key={role}
        className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600"
      >
        {roleLabelMap[role]}
      </span>
    ));

const userIdOf = (user: string | ShopUserData) =>
  typeof user === 'string' ? user : user._id;

const phoneEditOpenId = (userId: string) => `seller-shop-user-phone-edit-${userId}`;
const linkModalOpenId = (shopId: string) => `seller-shop-user-link-${shopId}`;

interface PhoneForm {
  phoneNumber: string;
}

//* 계정 전화번호 수정 모달 (admin/user PATCH)
const ShopUserPhoneEditModal: FC<{ user: ShopUserData; shopId: string }> = ({
  user,
  shopId,
}) => {
  const openId = phoneEditOpenId(user._id);
  const { sellerRole } = useAuth();
  const [open, setOpen] = useRecoilState(simpleOpenAtomFamily(openId));
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const queryClient = useQueryClient();

  const { register, control, handleSubmit, reset } = useForm<PhoneForm>({
    defaultValues: { phoneNumber: user.info?.phoneNumber ?? '' },
  });
  const telValue = useWatch({ control, name: 'phoneNumber', defaultValue: '' });

  //* 열 때마다 최신 번호로 리시드
  useEffect(() => {
    if (open) reset({ phoneNumber: user.info?.phoneNumber ?? '' });
  }, [open, user.info?.phoneNumber, reset]);

  const onCloseModal = useCallback(() => {
    setOpen(false);
    history.back();
  }, [setOpen]);

  const onSubmit: SubmitHandler<PhoneForm> = useCallback(
    async (data) => {
      const phoneNumber = String(data.phoneNumber).replace(/[^0-9]/g, '');
      if (!/^01[016789][0-9]{7,8}$/.test(phoneNumber)) {
        notificateError(notifiacationInstance, '올바른 휴대폰 번호를 입력해주세요.');
        return;
      }
      try {
        //* info는 중첩객체 통째 교체라 기존 값 spread 후 번호만 덮어쓴다
        await adminUpdateUser({
          _id: user._id,
          info: { ...(user.info ?? {}), phoneNumber },
        });
        queryClient.invalidateQueries(sellerShopHookUrl(shopId, sellerRole()));
        notificateSuccess(notifiacationInstance, '전화번호가 수정되었습니다.');
        onCloseModal();
      } catch (err) {
        console.error(err);
        notificateError(notifiacationInstance, '전화번호 수정에 실패했습니다.');
      }
    },
    [
      user._id,
      user.info,
      notifiacationInstance,
      onCloseModal,
      queryClient,
      sellerRole,
      shopId,
    ],
  );

  return (
    <Modal openId={openId}>
      <div className="space-y-4">
        <div>
          <p className="text-lg font-bold">전화번호 수정</p>
          <p className="break-keep pt-1 text-sm text-gray-600">
            {user.info?.name ?? user.username} ({user.username}) 계정의 전화번호를
            수정합니다.
          </p>
        </div>
        <TelField
          value={String(telValue)}
          register={register}
          name="phoneNumber"
          className="w-full font-bold"
          required={true}
        />
        <Button
          className="button-rectangle min-h-[40px] theme-text-4 theme-bg-main"
          onClick={handleSubmit(onSubmit)}
        >
          수정
        </Button>
      </div>
    </Modal>
  );
};

const SellerShopUserList: FC<IProps> = ({ shop }: IProps) => {
  const { sellerRole } = useAuth();
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const queryClient = useQueryClient();
  const setLinkOpen = useSetRecoilState(simpleOpenAtomFamily(linkModalOpenId(shop._id)));

  // 구서버 등으로 populate가 안 된 경우 string 항목은 제외
  const userList = useMemo(
    () =>
      (shop.users ?? []).filter(
        (user): user is ShopUserData => typeof user !== 'string' && !!user,
      ),
    [shop.users],
  );

  //* 서버 기준 연결 id 배열 (admin/shop PATCH는 $set 전체 교체라 항상 완전한 배열을 보내야 함)
  const serverIds = useMemo(() => (shop.users ?? []).map(userIdOf), [shop.users]);

  //* 방금 서버에 보낸 권위 id 배열. refetch가 반영되기 전 연속 조작이 이전 변경을
  //* 누락하지 않도록 ref로 최신값을 읽는다. null이면 서버값(serverIds)에 정렬.
  const pendingIdsRef = useRef<string[] | null>(null);
  const busyRef = useRef(false);
  //* 모달의 '연결됨' 표시를 낙관적으로 갱신하기 위한 미러 상태
  const [optimisticIds, setOptimisticIds] = useState<string[] | null>(null);

  //* 서버 데이터가 새로 들어오고(외부 변경 포함) 진행 중이 아니면 서버값으로 재정렬
  useEffect(() => {
    if (!busyRef.current) {
      pendingIdsRef.current = null;
      setOptimisticIds(null);
    }
  }, [serverIds]);

  const readIds = useCallback(
    () => pendingIdsRef.current ?? serverIds,
    [serverIds],
  );
  const commitIds = useCallback((ids: string[] | null) => {
    pendingIdsRef.current = ids;
    setOptimisticIds(ids);
  }, []);

  //* 화면/모달 표시에 쓰는 유효 연결 id 집합 (낙관값 우선)
  const linkedIds = useMemo(
    () => new Set(optimisticIds ?? serverIds),
    [optimisticIds, serverIds],
  );

  const persistUsers = useCallback(
    async (nextIds: string[], successMsg: string, failMsg: string) => {
      busyRef.current = true;
      commitIds(nextIds);
      try {
        const updated = await updateShop(
          { _id: shop._id, users: nextIds },
          sellerRole(),
        );
        if (!updated) {
          //* 실패 시 서버는 그대로이므로 낙관값을 버리고 서버 기준으로 재정렬
          commitIds(null);
          notificateError(notifiacationInstance, failMsg);
          return;
        }
        notificateSuccess(notifiacationInstance, successMsg);
        //* refetch 완료까지 기다려 busy(버튼 비활성)를 유지 → 연속 조작 레이스 방지
        await queryClient.invalidateQueries(sellerShopHookUrl(shop._id, sellerRole()));
      } finally {
        busyRef.current = false;
      }
    },
    [shop._id, sellerRole, queryClient, notifiacationInstance, commitIds],
  );

  //* shop.users에서 해당 유저만 제거 (계정 삭제 아님)
  const [{ loading: unlinking }, unlinkUser] = useAsyncFn(
    async (target: ShopUserData) => {
      const targetName = target.info?.name ?? target.username;
      const confirmed = window.confirm(
        `'${targetName}(${target.username})' 계정을 이 판매자에서 연결해제하시겠습니까?\n연결해제하면 해당 계정으로 이 판매자 페이지에 접근할 수 없습니다. (계정 자체는 삭제되지 않습니다)`,
      );
      if (!confirmed) return;

      const next = readIds().filter((id) => id !== target._id);
      await persistUsers(next, '연결해제되었습니다.', '연결해제에 실패했습니다.');
    },
    [readIds, persistUsers],
  );

  //* 검색으로 고른 유저를 shop.users에 추가 (기존 id + 신규 id)
  const [{ loading: linking }, linkUser] = useAsyncFn(
    async (target: User) => {
      const base = readIds();
      if (base.includes(target._id)) {
        notificateError(notifiacationInstance, '이미 연결된 계정입니다.');
        return;
      }
      const next = [...base, target._id];
      await persistUsers(next, '계정을 연결했습니다.', '연결에 실패했습니다.');
    },
    [readIds, persistUsers, notifiacationInstance],
  );

  //* 낙관 집합에서 빠진 계정은 즉시 목록에서 제거 (연결해제 결과를 refetch 전 바로 반영)
  const visibleUsers = useMemo(
    () => userList.filter((user) => linkedIds.has(user._id)),
    [userList, linkedIds],
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="font-bold">연결된 계정</p>
        <Button
          className="button-rectangle min-h-[32px] w-auto px-3 text-sm theme-text-4 theme-bg-main"
          onClick={() => setLinkOpen(true)}
        >
          추가 연결
        </Button>
      </div>
      {visibleUsers.length === 0 ? (
        <p className="rounded-lg border border-dashed p-4 text-center text-sm text-gray-400">
          연결된 계정이 없습니다.
        </p>
      ) : (
        <div className="space-y-2">
          {visibleUsers.map((user) => (
            <ShopUserCard
              key={user._id}
              user={user}
              shopId={shop._id}
              unlinking={unlinking}
              onUnlink={() => unlinkUser(user)}
            />
          ))}
        </div>
      )}
      <ShopUserLinkModal
        shopId={shop._id}
        linkedIds={linkedIds}
        linking={linking}
        onLink={linkUser}
      />
    </div>
  );
};

//* 계정 검색·추가 연결 모달
const ShopUserLinkModal: FC<{
  shopId: string;
  linkedIds: Set<string>;
  linking: boolean;
  onLink: (user: User) => void;
}> = ({ shopId, linkedIds, linking, onLink }) => {
  const openId = linkModalOpenId(shopId);
  const open = useRecoilValue(simpleOpenAtomFamily(openId));
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);

  const [keyword, setKeyword] = useState('');

  const [{ loading, value: results }, search] = useAsyncFn(
    async (kw: string) => {
      try {
        return await adminSellerUserSearch(kw);
      } catch (err) {
        console.error(err);
        notificateError(notifiacationInstance, '검색에 실패했습니다.');
        return [] as User[];
      }
    },
    [notifiacationInstance],
  );

  //* 모달 열릴 때 전체(최근순) 1회 로드
  useEffect(() => {
    if (open) {
      setKeyword('');
      search('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') search(keyword);
  };

  return (
    <Modal openId={openId} position="center">
      <div className="w-[92vw] max-w-md space-y-4">
        <div>
          <p className="text-lg font-bold">계정 추가 연결</p>
          <p className="break-keep pt-1 text-sm text-gray-600">
            판매자·판매자 관리자·판매자 직원 계정을 이름·아이디·전화번호로 검색해 이
            판매자에 연결합니다.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="이름 / 아이디 / 전화번호"
            className="w-full rounded-lg border px-3 py-2 text-sm font-bold theme-text-1 theme-bg-1 theme-border-1"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <Button
            className="button-rectangle min-h-[36px] w-auto shrink-0 px-4 text-sm theme-text-4 theme-bg-main"
            onClick={() => search(keyword)}
          >
            검색
          </Button>
        </div>

        <div className="max-h-[50vh] space-y-2 overflow-y-auto">
          {loading && (
            <div className="center-box h-24">
              <Loading />
            </div>
          )}
          {!loading && results && results.length === 0 && (
            <p className="py-6 text-center text-sm text-gray-400">
              검색 결과가 없습니다.
            </p>
          )}
          {!loading &&
            results?.map((user) => {
              const linked = linkedIds.has(user._id);
              return (
                <div
                  key={user._id}
                  className="flex items-center justify-between gap-2 rounded-lg border p-3"
                >
                  <div className="min-w-0 space-y-0.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-bold">
                        {user.info?.name ?? user.username}
                      </span>
                      {roleBadges(user.roles)}
                    </div>
                    <p className="truncate text-xs text-gray-500">
                      {user.username}
                      {user.info?.phoneNumber
                        ? ` · ${phoneNumberStringFormator(user.info.phoneNumber)}`
                        : ''}
                    </p>
                  </div>
                  {linked ? (
                    <span className="shrink-0 text-xs font-medium text-gray-400">
                      연결됨
                    </span>
                  ) : (
                    <Button
                      className="button-rectangle min-h-[32px] w-auto shrink-0 px-3 text-sm theme-text-4 theme-bg-main"
                      disabled={linking}
                      onClick={() => onLink(user)}
                    >
                      연결
                    </Button>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </Modal>
  );
};

//* 계정 카드 (전화번호 수정 모달 open 상태를 per-user로 관리)
const ShopUserCard: FC<{
  user: ShopUserData;
  shopId: string;
  unlinking: boolean;
  onUnlink: () => void;
}> = ({ user, shopId, unlinking, onUnlink }) => {
  const setPhoneEditOpen = useSetRecoilState(
    simpleOpenAtomFamily(phoneEditOpenId(user._id)),
  );

  return (
    <div className="space-y-1 rounded-lg border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-bold">{user.info?.name ?? user.username}</p>
        {roleBadges(user.roles)}
        {user.isDeleted && (
          <span className="rounded bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-500">
            삭제됨
          </span>
        )}
      </div>
      <div className="grid grid-cols-5 items-center gap-4 text-sm">
        <p className="col-span-2 text-gray-500">아이디</p>
        <p className="col-span-3 font-medium">{user.username}</p>
      </div>
      <div className="grid grid-cols-5 items-center gap-4 text-sm">
        <p className="col-span-2 text-gray-500">전화번호</p>
        <p className="col-span-3 font-medium">
          {phoneNumberStringFormator(user.info?.phoneNumber)}
        </p>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button
          className="button-rectangle min-h-[32px] w-auto px-3 text-sm theme-text-4 theme-bg-main"
          onClick={() => setPhoneEditOpen(true)}
        >
          전화번호 수정
        </Button>
        <Button
          className="button-rectangle min-h-[32px] w-auto border border-red-500 bg-white px-3 text-sm text-red-500"
          disabled={unlinking}
          onClick={onUnlink}
        >
          연결해제
        </Button>
      </div>
      <ShopUserPhoneEditModal user={user} shopId={shopId} />
    </div>
  );
};

export default SellerShopUserList;
